import {Box, Typography} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import FormatPaintIcon from "@mui/icons-material/FormatPaint";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {ElevationApp} from "../ElevationApp";
import {MdAnchorSetup, MdSelection} from "../model/view/MdViewSettings";
import {useEventEmitter2} from "../util/useEventEmitter2";
import {RibbonButton, RibbonGroup, RibbonSelect, RibbonStepper} from "./Ribbon";
import {emitChanged} from "./selectedTab/helpers";
import {sticksById} from "../model/frame/MdFrameBlock";
import {applyAnchorProductToAll, clearAnchors, MIN_SECTION_LENGTH, setAnchorProduct, setAnchorQty, setAnchorSectionCount} from "../scenes/frameGrid/Anchors";

// true when the opening offers anchor setup: a secondary opening's frame (and so its
// anchors) is edited on its primary.
export function anchorSetupAllowed(app: ElevationApp): boolean {
    return !app.secondaryOf.value;
}

// enter anchor setup. The paint product starts as the opening's anchor product, else the
// company default.
export function startAnchorSetup(app: ElevationApp) {
    const vs = app.viewSettings;
    const paint = app.opening?.frameAnchorProduct || app.anchorProducts.value?.defaultProduct || null;

    vs.clearSelection();
    vs.anchorSetup.emit({paintProduct: paint});
}

export function endAnchorSetup(app: ElevationApp) {
    app.viewSettings.clearSelection();
    app.viewSettings.anchorSetup.emit(null);
}

type AnchorSel = Extract<MdSelection, {type: "anchor-stick" | "anchor-section"}>;

// the ribbon while the user sets up frame anchors. It is the only tab shown; the user
// leaves with Done (or Esc).
export function AnchorSetupTab(props: {app: ElevationApp; setup: MdAnchorSetup}) {
    const app = props.app;
    useEventEmitter2(app.frameSpec.frameBlocksChanged);
    const products = useEventEmitter2(app.anchorProducts);
    const selection = useEventEmitter2(app.viewSettings.selection) ?? [];

    const options = (products?.products ?? []).map(p => ({value: p.id.toString(), label: `${p.name} (${p.productCode})`}));
    const withNone = (value: number | null) => value === null || !options.some(o => o.value === value.toString())
        ? [{value: value?.toString() ?? "", label: value === null ? "None" : "#" + value}, ...options]
        : options;

    const paint = props.setup.paintProduct;
    const sel = selection.find((s): s is AnchorSel => s.type === "anchor-stick" || s.type === "anchor-section");

    return (
        <>
            <RibbonGroup label="Paint Anchor">
                <RibbonSelect
                    value={paint?.toString() ?? ""}
                    options={withNone(paint)}
                    onChange={v => app.viewSettings.anchorSetup.emit({paintProduct: v ? parseInt(v) : null})}
                />
                <RibbonButton
                    icon={<FormatPaintIcon />}
                    label="Apply to All"
                    disabled={paint === null}
                    onClick={() => {
                        if(paint !== null && applyAnchorProductToAll(app.frameSpec, paint)) emitChanged(app);
                    }}
                />
            </RibbonGroup>

            {sel ? <AnchorStickGroups app={app} sel={sel} withNone={withNone} /> : (
                <RibbonGroup label="Anchors">
                    <Box sx={{display: "flex", alignItems: "center", height: "100%", px: 1, maxWidth: 320}}>
                        <Typography sx={{fontSize: 13}}>
                            Click an outside stick to add anchors with the paint anchor. Click a stick with anchors to edit them.
                        </Typography>
                    </Box>
                </RibbonGroup>
            )}

            <RibbonGroup label="Actions">
                <RibbonButton icon={<CheckIcon />} label="Done" shortcut="Esc" onClick={() => endAnchorSetup(app)} />
            </RibbonGroup>
        </>
    );
}

// the controls for the selected stick's anchors, or for one of its sections.
function AnchorStickGroups(props: {
    app: ElevationApp;
    sel: AnchorSel;
    withNone: (value: number | null) => {value: string; label: string}[];
}) {
    const app = props.app;
    const stickId = props.sel.stickId;
    const anchors = sticksById(app.frameSpec.frameBlocks).get(stickId)?.anchors;
    const layout = app.frameGrid?.anchorMarks.layoutOf(stickId) ?? null;
    if(!anchors || !layout) return null;

    const sections = anchors.sections;
    // one section is edited when it is selected, or when the stick has only one.
    const index = props.sel.type === "anchor-section" ? props.sel.index : sections.length === 1 ? 0 : null;
    const section = index !== null ? sections[index] : null;
    const resolved = index !== null ? layout.sections[index] : null;

    const edit = (changed: boolean) => {
        if(changed) emitChanged(app);
    };

    const product = section?.product ?? (sections.every(s => s.product === sections[0].product) ? sections[0].product : null);
    const maxSections = Math.max(1, Math.floor(layout.length / MIN_SECTION_LENGTH));

    return (
        <>
            <RibbonGroup label={index !== null && sections.length > 1 ? `Section ${index + 1}` : "Product"}>
                <RibbonSelect
                    value={product?.toString() ?? ""}
                    options={props.withNone(product)}
                    onChange={v => v && edit(setAnchorProduct(app.frameSpec, stickId, index, parseInt(v)))}
                />
            </RibbonGroup>

            {section && resolved ? (
                <RibbonGroup label={section.qty === undefined ? "Qty (auto)" : "Qty"}>
                    <RibbonStepper
                        value={resolved.qty}
                        min={0}
                        onChange={qty => edit(setAnchorQty(app.frameSpec, stickId, index!, qty))}
                    />
                    <RibbonButton
                        icon={<RestartAltIcon />}
                        label="Auto"
                        active={section.qty === undefined}
                        onClick={() => edit(setAnchorQty(app.frameSpec, stickId, index!, undefined))}
                    />
                </RibbonGroup>
            ) : (
                <RibbonGroup label="Qty">
                    <Box sx={{display: "flex", alignItems: "center", height: "100%", px: 1, maxWidth: 200}}>
                        <Typography sx={{fontSize: 13}}>
                            {layout.sections.reduce((sum, s) => sum + s.qty, 0)} total. Click a section in the strip to set its qty.
                        </Typography>
                    </Box>
                </RibbonGroup>
            )}

            <RibbonGroup label="Sections">
                <RibbonStepper
                    value={sections.length}
                    min={1}
                    max={maxSections}
                    onChange={count => {
                        if(!setAnchorSectionCount(app.frameSpec, stickId, count, layout.length)) return;
                        app.viewSettings.selectItem({type: "anchor-stick", id: "anchor:" + stickId, stickId}, false);
                        emitChanged(app);
                    }}
                />
                <RibbonButton
                    icon={<DeleteOutlineIcon />}
                    label="Clear Anchors"
                    onClick={() => {
                        if(!clearAnchors(app.frameSpec, stickId)) return;
                        app.viewSettings.clearSelection();
                        emitChanged(app);
                    }}
                />
            </RibbonGroup>
        </>
    );
}
